import ArrowIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Box,
    Button,
    Dialog,
    Fade,
    IconButton,
    Paper,
    Tooltip,
    Typography
} from '@mui/material';
import React, { useState, useEffect } from 'react';

/**
 * A transparent help overlay that explains UI components
 * 
 * @param {Object} props Component props
 * @param {Object[]} props.helpItems Array of help items with target selectors and descriptions
 * @param {string} props.title The title for the help overlay
 * @param {boolean} props.open External control for open state
 * @param {function} props.onClose External control for close action
 * @param {boolean} props.showButton Whether to show the help button
 * @param {string} props.buttonId ID for the help button
 */
const HelpOverlay = ({ 
    helpItems = [], 
    title = "Help Guide",
    open: externalOpen, 
    onClose: externalClose,
    showButton = true,
    buttonId = 'help-overlay-button' // Add button ID parameter
}) => {
    const [open, setOpen] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    
    const handleOpen = () => {
        setOpen(true);
        setCurrentStep(0);
    };
    
    const handleClose = () => {
        setOpen(false);
    };
    
    const handleNext = () => {
        if (currentStep < helpItems.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            handleClose();
        }
    };
    
    const handlePrevious = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    return (
        <>
            {showButton && (
                <Tooltip title="Help Guide">
                    <IconButton 
                        id={buttonId}
                        onClick={handleOpen} 
                        color="primary" 
                        sx={{ 
                            position: 'fixed', 
                            bottom: 20, 
                            right: 20, 
                            zIndex: 1000,
                            backgroundColor: 'white',
                            boxShadow: 2,
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.9)'
                            }
                        }}
                    >
                        <HelpOutlineIcon />
                    </IconButton>
                </Tooltip>
            )}
            
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'hidden',
                        height: '100%'
                    }
                }}
            >
                <Box sx={{ 
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', // Slightly darker overlay for better contrast
                    zIndex: 1200,
                    pointerEvents: 'none'
                }} />
                
                {helpItems.length > 0 && open && (
                    <Fade in={open}>
                        <Box>
                            {helpItems.map((item, index) => (
                                <HelpItem
                                    key={index}
                                    item={item}
                                    visible={currentStep === index}
                                    onClose={handleClose}
                                />
                            ))}
                            
                            <Paper sx={{ 
                                position: 'fixed', 
                                bottom: 20, 
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                justifyContent: 'center',
                                zIndex: 1300,
                                pointerEvents: 'auto',
                                p: 1.5,
                                borderRadius: 2,
                                boxShadow: 3,
                                width: 'auto',
                                minWidth: '250px'
                            }}>
                                <Button 
                                    onClick={handlePrevious}
                                    disabled={currentStep === 0}
                                    variant="outlined"
                                    sx={{ mr: 1 }}
                                >
                                    Previous
                                </Button>
                                <Button 
                                    onClick={handleNext}
                                    variant="contained"
                                    color="primary"
                                >
                                    {currentStep < helpItems.length - 1 ? 'Next' : 'Finish'}
                                </Button>
                            </Paper>
                            
                            <Typography sx={{ 
                                position: 'fixed',
                                bottom: 80,
                                left: 0,
                                right: 0,
                                textAlign: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                zIndex: 1300,
                                textShadow: '0 1px 3px rgba(0,0,0,0.8)', // Text shadow for better visibility
                                fontSize: '0.9rem'
                            }}>
                                Step {currentStep + 1} of {helpItems.length}
                            </Typography>
                        </Box>
                    </Fade>
                )}
            </Dialog>
        </>
    );
};

/**
 * Individual help item that highlights and explains a component
 */
const HelpItem = ({ item, visible, onClose }) => {
    const [position, setPosition] = useState({});
    const [elementRect, setElementRect] = useState(null);
    const [arrowProps, setArrowProps] = useState({});

    useEffect(() => {
        if (!visible) return;

        // Find the element and position the tooltip
        const targetEl = document.querySelector(item.selector);
        if (targetEl) {
            // Add highlighting to the element
            targetEl.setAttribute('data-help-highlight', 'true');
            targetEl.style.outline = '3px solid rgba(25, 118, 210, 0.7)';
            targetEl.style.outlineOffset = '2px';
            
            // Get element position
            const rect = targetEl.getBoundingClientRect();
            setElementRect(rect);
            
            // Calculate tooltip position with minimum width guarantees
            const pos = calculatePosition(rect, item.position || 'auto');
            setPosition(pos);
            
            // Calculate arrow position
            const arrow = calculateArrowProps(pos, rect, item.position || 'auto');
            setArrowProps(arrow);
            
            // Scroll element into view if needed
            if (!isElementInViewport(targetEl)) {
                targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
        
        return () => {
            // Clean up highlighting
            const targetEl = document.querySelector(item.selector);
            if (targetEl) {
                targetEl.removeAttribute('data-help-highlight');
                targetEl.style.outline = '';
                targetEl.style.outlineOffset = '';
            }
        };
    }, [item.selector, visible, item.position]);

    // Check if element is in viewport
    const isElementInViewport = (el) => {
        const rect = el.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= window.innerHeight &&
            rect.right <= window.innerWidth
        );
    };
    
    // Calculate the tooltip position based on the element position
    const calculatePosition = (rect, preferredPosition) => {
        const MARGIN = 20;
        const MIN_WIDTH = 280; // Minimum width for the tooltip
        const MAX_WIDTH = 350; // Maximum width for the tooltip
        
        // Calculate available space in each direction
        const spaceRight = window.innerWidth - rect.right - MARGIN;
        const spaceLeft = rect.left - MARGIN;
        const spaceTop = rect.top - MARGIN;
        const spaceBottom = window.innerHeight - rect.bottom - MARGIN;

        // Use preferred position if specified and if there's enough space
        switch(preferredPosition) {
            case 'right':
                if (spaceRight >= MIN_WIDTH) {
                    return {
                        left: rect.right + MARGIN,
                        top: rect.top + (rect.height / 2),
                        transform: 'translateY(-50%)',
                        maxWidth: `${Math.min(MAX_WIDTH, spaceRight)}px`,
                        minWidth: `${MIN_WIDTH}px`
                    };
                }
                break;
                
            case 'left':
                if (spaceLeft >= MIN_WIDTH) {
                    return {
                        right: window.innerWidth - rect.left + MARGIN,
                        top: rect.top + (rect.height / 2),
                        transform: 'translateY(-50%)',
                        maxWidth: `${Math.min(MAX_WIDTH, spaceLeft)}px`,
                        minWidth: `${MIN_WIDTH}px`
                    };
                }
                break;
                
            case 'top':
                if (spaceTop >= 100) { // Minimum height for tooltip
                    return {
                        bottom: window.innerHeight - rect.top + MARGIN,
                        left: rect.left + (rect.width / 2),
                        transform: 'translateX(-50%)',
                        maxWidth: `${MAX_WIDTH}px`,
                        minWidth: `${MIN_WIDTH}px`
                    };
                }
                break;
                
            case 'bottom':
                if (spaceBottom >= 100) {
                    return {
                        top: rect.bottom + MARGIN,
                        left: rect.left + (rect.width / 2),
                        transform: 'translateX(-50%)',
                        maxWidth: `${MAX_WIDTH}px`,
                        minWidth: `${MIN_WIDTH}px`
                    };
                }
                break;
                
            default:
                break;
        }
        
        // If preferred position doesn't have enough space or no preference was specified,
        // automatically choose the best position based on available space
        const spaces = [
            { position: 'right', space: spaceRight },
            { position: 'left', space: spaceLeft },
            { position: 'bottom', space: spaceBottom },
            { position: 'top', space: spaceTop }
        ];
        
        // Sort by available space
        spaces.sort((a, b) => b.space - a.space);
        
        // Choose the position with the most space
        const bestPosition = spaces[0].position;
        
        switch(bestPosition) {
            case 'right':
                return {
                    left: rect.right + MARGIN,
                    top: rect.top + (rect.height / 2),
                    transform: 'translateY(-50%)',
                    maxWidth: `${Math.min(MAX_WIDTH, spaceRight)}px`,
                    minWidth: `${MIN_WIDTH}px`
                };
                
            case 'left':
                return {
                    right: window.innerWidth - rect.left + MARGIN,
                    top: rect.top + (rect.height / 2),
                    transform: 'translateY(-50%)',
                    maxWidth: `${Math.min(MAX_WIDTH, spaceLeft)}px`,
                    minWidth: `${MIN_WIDTH}px`
                };
                
            case 'top':
                return {
                    bottom: window.innerHeight - rect.top + MARGIN,
                    left: rect.left + (rect.width / 2),
                    transform: 'translateX(-50%)',
                    maxWidth: `${MAX_WIDTH}px`,
                    minWidth: `${MIN_WIDTH}px`
                };
                
            case 'bottom':
            default:
                return {
                    top: rect.bottom + MARGIN,
                    left: rect.left + (rect.width / 2),
                    transform: 'translateX(-50%)',
                    maxWidth: `${MAX_WIDTH}px`,
                    minWidth: `${MIN_WIDTH}px`
                };
        }
    };
    
    // Calculate arrow properties based on tooltip position
    const calculateArrowProps = (tooltipPos, elementRect, preferredPosition) => {
        // Determine which side of the tooltip the arrow should be on
        if (tooltipPos.left && tooltipPos.right === undefined && 
            tooltipPos.transform && tooltipPos.transform.includes('translateY')) {
            // Arrow on left side (tooltip is on the right of element)
            return {
                position: 'left',
                sx: {
                    left: -20,
                    top: '50%',
                    transform: 'translateY(-50%) rotate(180deg)',
                    fontSize: '3rem',
                    color: '#1976d2' // Primary color
                }
            };
        }
        
        if (tooltipPos.right && tooltipPos.left === undefined &&
            tooltipPos.transform && tooltipPos.transform.includes('translateY')) {
            // Arrow on right side (tooltip is on the left of element)
            return {
                position: 'right',
                sx: {
                    right: -20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '3rem',
                    color: '#1976d2'
                }
            };
        }
        
        if (tooltipPos.top && tooltipPos.bottom === undefined &&
            tooltipPos.transform && tooltipPos.transform.includes('translateX')) {
            // Arrow on top side (tooltip is below element)
            return {
                position: 'top',
                sx: {
                    top: -20,
                    left: '50%',
                    transform: 'translateX(-50%) rotate(270deg)',
                    fontSize: '3rem',
                    color: '#1976d2'
                }
            };
        }
        
        if (tooltipPos.bottom && tooltipPos.top === undefined &&
            tooltipPos.transform && tooltipPos.transform.includes('translateX')) {
            // Arrow on bottom side (tooltip is above element)
            return {
                position: 'bottom',
                sx: {
                    bottom: -20,
                    left: '50%',
                    transform: 'translateX(-50%) rotate(90deg)',
                    fontSize: '3rem',
                    color: '#1976d2'
                }
            };
        }
        
        // Default arrow (if position couldn't be determined)
        return {
            position: 'left',
            sx: {
                left: -20,
                top: '50%',
                transform: 'translateY(-50%) rotate(180deg)',
                fontSize: '3rem',
                color: '#1976d2'
            }
        };
    };

    // Render the help item only when visible
    if (!visible) return null;
    
    // Process text to format bullet points nicely
    const processDescription = (text) => {
        if (!text) return '';
        
        // Replace bullet points with proper formatting
        let formattedText = text;
        
        // Replace bullet points (•) followed by text
        if (formattedText.includes('• ')) {
            const parts = formattedText.split('• ');
            const firstPart = parts.shift(); // Get text before first bullet
            
            formattedText = firstPart + parts.map(part => {
                // Find where the bullet point text ends (at newline or end of string)
                const lineBreakPos = part.indexOf('\n');
                if (lineBreakPos === -1) {
                    // No line break, this is a simple bullet point
                    return `<li>${part}</li>`;
                } else {
                    // Line break found, wrap just the bullet point text
                    const bulletText = part.substring(0, lineBreakPos);
                    const restOfText = part.substring(lineBreakPos);
                    return `<li>${bulletText}</li>${restOfText}`;
                }
            }).join('');
            
            // Wrap list items with ul tags
            if (formattedText.includes('<li>')) {
                formattedText = formattedText.replace(/<li>/g, '<ul style="margin: 0; padding-left: 20px"><li>');
                formattedText = formattedText.replace(/<\/li>/g, '</li></ul>');
                // Fix nested lists
                formattedText = formattedText.replace(/<\/ul><ul style="margin: 0; padding-left: 20px">/g, '');
            }
        }
        
        return formattedText;
    };

    return (
        <React.Fragment>
            {/* Tooltip box */}
            <Paper
                elevation={6}
                sx={{
                    position: 'fixed',
                    ...position,
                    backgroundColor: 'white',
                    borderRadius: 2,
                    p: 2.5,
                    zIndex: 1300,
                    pointerEvents: 'auto',
                    border: '2px solid',
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    overflow: 'hidden', // Prevent content overflow
                    maxWidth: position.maxWidth || '320px',
                    minWidth: position.minWidth || '280px',
                    width: 'auto'
                }}
            >
                {/* Close button */}
                <IconButton
                    size="small"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'text.secondary'
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
                
                {/* Title */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 600,
                        mb: 1.5,
                        color: 'primary.main',
                        pr: 3 // Space for close button
                    }}
                >
                    {item.title}
                </Typography>
                
                {/* Description with bullet point support */}
                <Box 
                    sx={{
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                        color: 'text.primary',
                        '& ul': {
                            mt: 0.5,
                            mb: 0.5
                        },
                        '& li': {
                            mb: 0.5
                        }
                    }}
                    dangerouslySetInnerHTML={{ __html: processDescription(item.description) }}
                />

                {/* Animated arrow with backdrop for visibility */}
                <Box
                    sx={{
                        position: 'absolute',
                        ...arrowProps.sx,
                        zIndex: 1301,
                        animation: 'pulse-arrow 1.5s infinite alternate',
                        '&:before': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: '2.2rem',
                            height: '2.2rem',
                            borderRadius: '50%',
                            backgroundColor: 'white',
                            transform: 'translate(-50%, -50%)',
                            boxShadow: '0 0 12px rgba(0,0,0,0.2)',
                            zIndex: -1
                        }
                    }}
                >
                    <ArrowIcon 
                        sx={{
                            fontSize: 'inherit',
                            filter: 'drop-shadow(0 0 3px rgba(25,118,210,0.5))'
                        }}
                    />
                </Box>
                
                {/* Keyframe animations */}
                <style>
                    {`
                    @keyframes pulse-arrow {
                        0% { transform: ${arrowProps.sx?.transform} scale(1); opacity: 0.8; }
                        100% { transform: ${arrowProps.sx?.transform} scale(1.15); opacity: 1; }
                    }
                    `}
                </style>
            </Paper>
        </React.Fragment>
    );
};

export default HelpOverlay;